/**
 * CSV を取り込んだあと業務画面へ戻る仕組みの純関数 (全種別共通)。
 *
 * デザイナー決定 (2026-10-15): 「CSV 入出力 > 全画面共通、取り込みが完了したら対象の画面に
 * スムーズに戻り、反映された内容を確認できる UX」。プロトタイプの `js/csv-import-return.js`
 * (enechain-labs/ebalance-prototype#635) と同じ流れにする:
 * 1. 業務画面の CSV アップロード導線は、取り込み画面の URL に `?returnTo=<今の画面の path + search>` を付ける
 * 2. 取り込み画面は returnTo を読む。キャンセルと取り込み後の戻り先にする (無ければ従来どおり)
 * 3. 取り込み後は router の location state で件数を業務画面へ渡し、業務画面の上部に 1 回だけ Alert を出す
 */

/** 取り込み画面の URL で戻り先を渡すクエリの名前 */
export const CSV_IMPORT_RETURN_TO_PARAM = 'returnTo'

/** 成功のトーストを見せてから業務画面へ移るまでの時間 (プロトタイプと同じ 1.5 秒) */
export const CSV_IMPORT_RETURN_DELAY_MS = 1500

const MAX_RETURN_TO_LENGTH = 2000

/** 取り込んだ CSV の種別。業務画面の Alert は自分の種別の結果だけを出す */
export type CsvImportKind =
  | 'supply48Data'
  | 'bilateralTrade'
  | 'cisContract'
  | 'demandGroups'
  | 'customerContract'

/** 件数の語は件数サマリ (spec admin/data/uploads/common.md AC-DUC-013) にそろえる */
export type CsvImportAppliedCounts = {
  created: number
  updated: number
  deleted: number
}

export type CsvImportAppliedNotice = {
  kind: CsvImportKind
  counts: CsvImportAppliedCounts
}

/** 業務画面へ移る時の location state の形 */
export type CsvImportReturnState = {
  csvImportApplied: CsvImportAppliedNotice
}

/**
 * returnTo を同じオリジンの相対パス (先頭が `/` 1 個) だけに絞る。それ以外は無いものとして扱う。
 * 外部サイトへの遷移 (`//evil.example` や `https://…`) と、取り込み画面自身への戻りを防ぐ。
 */
export function sanitizeCsvImportReturnTo(
  raw: string | null | undefined,
  currentPathname: string,
): string | undefined {
  if (
    raw === null ||
    raw === undefined ||
    raw === '' ||
    raw.length > MAX_RETURN_TO_LENGTH
  ) {
    return undefined
  }
  if (!raw.startsWith('/') || raw.startsWith('//')) return undefined
  // バックスラッシュはブラウザが `/` として読むことがあり (`/\evil.example`)、制御文字は URL を壊す
  // eslint-disable-next-line no-control-regex -- 制御文字を弾くための文字クラス
  if (/[\u0000-\u001f\\]/.test(raw)) return undefined
  let url: URL
  try {
    url = new URL(raw, window.location.origin)
  } catch {
    return undefined
  }
  if (url.origin !== window.location.origin) return undefined
  if (url.pathname === currentPathname) return undefined
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * 取り込み画面の path に戻り先を付ける。 path が既に query を持つ時 (入札 CSV の `?kind=normal` 等) は
 * `&` でつなぐ。
 */
export function withCsvImportReturnTo(
  importPath: string,
  returnTo: string,
): string {
  const params = new URLSearchParams({ [CSV_IMPORT_RETURN_TO_PARAM]: returnTo })
  const separator = importPath.includes('?') ? '&' : '?'
  return `${importPath}${separator}${params.toString()}`
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

/**
 * location state から、指定した種別の取り込み結果だけを読む。形が違うもの・別の種別のものは無視する
 * (location state は履歴に残るので、型を信用せずに確かめる)。
 */
export function readCsvImportAppliedNotice(
  state: unknown,
  kind: CsvImportKind,
): CsvImportAppliedNotice | undefined {
  if (typeof state !== 'object' || state === null) return undefined
  const notice: unknown = (state as Record<string, unknown>).csvImportApplied
  if (typeof notice !== 'object' || notice === null) return undefined
  const { kind: noticeKind, counts } = notice as Record<string, unknown>
  if (noticeKind !== kind) return undefined
  if (typeof counts !== 'object' || counts === null) return undefined
  const { created, updated, deleted } = counts as Record<string, unknown>
  if (!isCount(created) || !isCount(updated) || !isCount(deleted)) {
    return undefined
  }
  return { kind, counts: { created, updated, deleted } }
}
